import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {filter, Subscription} from 'rxjs';
import {LocalStorageService} from '@services/local-storage.service';
import {OTHER_THAN_CLIENT} from '@auth/constants';
import {PdfConverterService} from '../../../pages/pdf-converter/pdf-converter.service';
import {ProgressPopupComponent} from '../../../pages/pdf-converter/progress-popup/progress-popup.component';
import {PdfConverterProgressPopupService} from './pdf-converter-progress-popup.service';
import {ButtonComponent} from '../../../controls/button/button.component';

@Component({
  selector: 'app-pdf-converter-monitor',
  standalone: true,
  imports: [CommonModule, CdkConnectedOverlay, CdkOverlayOrigin, ProgressPopupComponent, ButtonComponent],
  template: `
      @if (canAccess && !isPdfConverterPage && (activeCount > 0 || progressPopupOpened)) {
          <div
                  cdkOverlayOrigin
                  #popupTrigger="cdkOverlayOrigin"
                  class="progress-bubble-wrapper"
                  [class.trigger-hidden]="(progressPopupOpened || dismissedForSession) && activeCount > 0"
          >
              @if (activeCount > 0) {
                  <app-button
                          class="progress-bubble rounded-5"
                          variant="primary"
                          (click)="toggleProgressPopup()"
                  >
                      Processing {{activeCount}} PDF
                  </app-button>
              }
          </div>

          <ng-template
                  cdkConnectedOverlay
                  cdkConnectedOverlayPanelClass="pdf-converter-progress-overlay-panel"
                  [cdkConnectedOverlayPush]="true"
                  [cdkConnectedOverlayOffsetX]="-19"
                  [cdkConnectedOverlayOffsetY]="22"
                  [cdkConnectedOverlayPositions]="popupPositions"
                  [cdkConnectedOverlayOrigin]="popupTrigger"
                  [cdkConnectedOverlayOpen]="progressPopupOpened"
                  (overlayOutsideClick)="closeProgressPopup()"
          >
              @defer (when progressPopupOpened) {
                  <app-pdf-converter-progress-popup></app-pdf-converter-progress-popup>
              }
          </ng-template>
      }
  `,
  styles: [`
    .progress-bubble-wrapper {
      position: fixed;
      right: 6px;
      bottom: 6px;
      z-index: 1050;
      transition: opacity .15s;
    }

    .progress-bubble-wrapper.trigger-hidden {
      opacity: 0;
      pointer-events: none;
    }

    .progress-bubble {
      box-shadow: 0 4px 12px rgba(0, 0, 0, .2);
    }

    :host ::ng-deep .pdf-converter-progress-overlay-panel {
      z-index: 1055;
    }
  `],
})
export class PdfConverterMonitorComponent implements OnInit, OnDestroy {
  activeCount: number = 0;
  isPdfConverterPage: boolean = false;
  canAccess: boolean = false;
  progressPopupOpened: boolean = false;
  dismissedForSession: boolean = false;
  popupPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];

  private subscriptions: Subscription[] = [];

  constructor(
    private pdfConverterService: PdfConverterService,
    private progressPopupService: PdfConverterProgressPopupService,
    private localStorageService: LocalStorageService,
    private router: Router,
  ) {
    this.canAccess = OTHER_THAN_CLIENT.includes(this.localStorageService.getCurrentUserDetails().role);
  }

  ngOnInit() {
    if (!this.canAccess) {
      return;
    }

    this.progressPopupService.loadDismissedState();
    this.updateRouteFlag(this.router.url, false);
    this.subscriptions.push(
      this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.updateRouteFlag(event.urlAfterRedirects, true)),
      this.pdfConverterService.activeConversions$.subscribe((conversions) => {
        this.activeCount = conversions.length;
      }),
      this.progressPopupService.opened$.subscribe((opened) => this.progressPopupOpened = opened),
      this.progressPopupService.dismissedForSession$.subscribe((dismissed) => this.dismissedForSession = dismissed),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  toggleProgressPopup() {
    if (!this.activeCount || this.isPdfConverterPage || this.dismissedForSession) {
      return;
    }

    this.progressPopupService.toggle();
  }

  closeProgressPopup() {
    this.progressPopupService.close();
  }

  private updateRouteFlag(url: string, shouldLoadOnExit: boolean) {
    const wasPdfConverterPage = this.isPdfConverterPage;
    this.isPdfConverterPage = (url || '').startsWith('/pdf-converter');
    if (this.isPdfConverterPage) {
      this.closeProgressPopup();
    }

    const shouldLoad = !this.isPdfConverterPage && (!shouldLoadOnExit || wasPdfConverterPage);
    if (shouldLoad) {
      this.pdfConverterService.loadActiveConversions().subscribe();
    }
  }
}
